import { useState, useRef, useCallback, useEffect } from 'react';
import type { OrganizerCandidate } from '../../services/api/teamEvents';

const COLORS = [
  '#3B82F6', '#EF4444', '#10B981', '#F59E0B',
  '#8B5CF6', '#EC4899', '#06B6D4', '#F97316',
];

function formatName(user: { firstName: string | null; lastName: string | null }) {
  return [user.firstName, user.lastName].filter(Boolean).join(' ') || '—';
}

interface SpinWheelProps {
  candidates: OrganizerCandidate[];
  onSelect: (candidate: OrganizerCandidate) => void;
}

export function SpinWheel({ candidates, onSelect }: SpinWheelProps) {
  const [rotation, setRotation] = useState(0);
  const [spinning, setSpinning] = useState(false);
  const winnerRef = useRef<OrganizerCandidate | null>(null);
  const wheelRef = useRef<HTMLDivElement>(null);
  const hasSpunRef = useRef(false);

  // Freeze displayed candidates — only update before first spin or when spin() is called
  const [displayCandidates, setDisplayCandidates] = useState(candidates);
  useEffect(() => {
    if (!hasSpunRef.current) {
      setDisplayCandidates(candidates);
    }
  }, [candidates]);

  const handleTransitionEnd = useCallback((e: React.TransitionEvent) => {
    if (e.target !== wheelRef.current || e.propertyName !== 'transform') return;
    setSpinning(false);
    if (winnerRef.current) {
      onSelect(winnerRef.current);
      winnerRef.current = null;
    }
  }, [onSelect]);

  const spin = useCallback(() => {
    if (spinning || candidates.length === 0) return;

    hasSpunRef.current = true;

    // Update to latest candidates and reset rotation for a clean start
    setDisplayCandidates(candidates);
    setRotation(0);

    // Wait for the browser to render the reset (rotation=0, no transition)
    // before starting the spin animation
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        const segmentAngle = 360 / candidates.length;
        const winnerIndex = Math.floor(Math.random() * candidates.length);
        winnerRef.current = candidates[winnerIndex]!;

        const targetAngle = winnerIndex * segmentAngle + segmentAngle / 2;
        const fullSpins = 5 * 360;
        const newRotation = fullSpins + (360 - targetAngle);

        setSpinning(true);
        setRotation(newRotation);
      });
    });
  }, [spinning, candidates]);

  if (displayCandidates.length === 0) {
    return (
      <div className="flex items-center justify-center h-64 text-gray-500">
        No candidates available.
      </div>
    );
  }

  const segmentAngle = 360 / displayCandidates.length;

  // Build conic-gradient stops
  const gradientStops = displayCandidates
    .map((_, i) => {
      const color = COLORS[i % COLORS.length]!;
      const start = i * segmentAngle;
      const end = (i + 1) * segmentAngle;
      return `${color} ${start}deg ${end}deg`;
    })
    .join(', ');

  return (
    <div className="flex flex-col items-center gap-4">
      {/* Pointer triangle */}
      <div className="w-0 h-0 border-l-[10px] border-r-[10px] border-t-[20px] border-l-transparent border-r-transparent border-t-gray-800 -mb-2 z-10" />

      {/* Wheel */}
      <div
        ref={wheelRef}
        onTransitionEnd={handleTransitionEnd}
        className="relative rounded-full border-4 border-gray-800 overflow-hidden"
        style={{
          width: 735,
          height: 735,
          background: `conic-gradient(${gradientStops})`,
          transform: `rotate(${rotation}deg)`,
          transition: spinning
            ? 'transform 4s cubic-bezier(0.17, 0.67, 0.12, 0.99)'
            : 'none',
        }}
      >
        {/* Segment labels */}
        {displayCandidates.map((candidate, i) => {
          const midAngle = i * segmentAngle + segmentAngle / 2;
          const rad = ((midAngle - 90) * Math.PI) / 180;
          const r = 735 / 2;
          const textR = r * 0.58;
          const cx = r + Math.cos(rad) * textR;
          const cy = r + Math.sin(rad) * textR;
          return (
            <div
              key={candidate.id}
              className="absolute"
              style={{
                left: cx,
                top: cy,
                transform: `translate(-50%, -50%) rotate(${midAngle - 90}deg)`,
                width: r - textR,
              }}
            >
              <span className="text-white text-xs font-semibold drop-shadow-[0_1px_2px_rgba(0,0,0,0.5)] whitespace-nowrap">
                {formatName(candidate)}
              </span>
            </div>
          );
        })}
      </div>

      {/* Spin button */}
      <button
        onClick={spin}
        disabled={spinning}
        className="px-6 py-2 bg-gray-800 text-white rounded-full font-semibold hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
      >
        {spinning ? 'Spinning...' : 'Spin!'}
      </button>
    </div>
  );
}
